import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H2, H4 } from "../../../styles/Fonts";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import ActivePosition from "./ActivePosition";
import ClosedPosition from "./ClosedPosition";
import LabelWithHint from "../../../components/LabelWithHint";
import Pagination from "./Pagination";
import { motion } from "framer-motion";
import {
  clearUserRecords,
  getFactoryPositions,
  getPositionHistories,
  getPositionInfos,
} from "../../../redux/factoryAction";
import { ALL_PAIRS, PairProps } from "../../../utils/constant/pairs";
import { useWeb3React } from "@web3-react/core";
import {
  UserActivePositionProps,
  UserClosedPositionProps,
} from "../../../redux/factorySlice";
import { StateProps } from "../../../hooks/useUniswapPoolState";
import { isSupportedChain } from "../../../utils/constant/chains";
import useIsUnderCorrectNetwork from "../../../hooks/useIsUnderCorrectNetwork";

const ITEM_PER_PAGE = 4;

const currHeads = [
  { label: "Pair", hint: "" },
  {
    label: "Position Value",
    hint: "The USD value of your V3 liquidity position",
  },
  { label: "Fee APR", hint: "The annualized return of your fee income" },
];
const hisHeads = [
  { label: "Pair", hint: "" },
  { label: "Event", hint: "" },
  {
    label: "Open Amount",
    hint: "The initial investment you put in this position",
  },
  { label: "Close Amount", hint: "The closed value of this position" },
];

const YourBot: React.FC<{
  pair: PairProps;
  vaultBalance: number;
  updateVaultBalance: () => void;
  uniPoolState: StateProps;
}> = ({ pair, vaultBalance, updateVaultBalance, uniPoolState }) => {
  const {
    token: currency,
    decimal,
    wantToken,
    borrowToken,
    uniPoolAddress,
    lendingPoolAddress,
    factoryAddress,
    deployedChainId,
  } = pair;

  const selectedChainId = useAppSelector(
    (state) => state.account.selectedChainId
  );
  const activePositions = useAppSelector(
    (state) => state.factory.userActivePositions
  );
  const closedPositions = useAppSelector(
    (state) => state.factory.userClosedPositions
  );

  const { account, chainId, library } = useWeb3React();

  const isUnderCorrectNetwork = useIsUnderCorrectNetwork();

  const allActivePositions = useMemo(
    () => activePositions?.[deployedChainId]?.[factoryAddress] || [],
    [activePositions, deployedChainId, factoryAddress]
  );
  const allClosedPositions = useMemo(
    () => closedPositions?.[deployedChainId]?.[factoryAddress] || [],
    [closedPositions, deployedChainId, factoryAddress]
  );

  const [isShowActive, setIsShowActive] = useState<boolean>(true);

  const [closedPage, setClosedPage] = useState<number>(1);
  const [maxClosedPage, setMaxClosedPage] = useState<number>(1);
  const [closedPos, setClosedPos] = useState<UserClosedPositionProps[]>([]);

  const [activePage, setActivePage] = useState<number>(1);
  const [maxActivePage, setMaxActivePage] = useState<number>(1);
  const [activePos, setActivePos] = useState<UserActivePositionProps[]>([]);

  const dispatch = useAppDispatch();

  const updateUserActivePositions = useCallback(async () => {
    if (
      !(
        account &&
        factoryAddress &&
        wantToken &&
        borrowToken &&
        uniPoolAddress &&
        lendingPoolAddress &&
        chainId &&
        library &&
        isSupportedChain(chainId)
      )
    )
      return;

    if (
      !ALL_PAIRS[chainId].find((pair) => pair.factoryAddress === factoryAddress)
    )
      return;

    dispatch(
      getFactoryPositions(
        account,
        factoryAddress,
        wantToken.tokenAddress,
        wantToken.tokenDecimal,
        borrowToken.tokenAddress,
        borrowToken.tokenDecimal,
        uniPoolAddress,
        lendingPoolAddress,
        chainId,
        library
      )
    );
  }, [
    account,
    wantToken,
    borrowToken,
    uniPoolAddress,
    lendingPoolAddress,
    factoryAddress,
    dispatch,
    chainId,
    library,
  ]);

  const updateUserClosedPositions = useCallback(async () => {
    if (
      !(
        account &&
        factoryAddress &&
        uniPoolAddress &&
        wantToken &&
        borrowToken &&
        chainId &&
        library &&
        isSupportedChain(chainId)
      )
    )
      return;

    if (
      !ALL_PAIRS[chainId].find((pair) => pair.factoryAddress === factoryAddress)
    )
      return;

    dispatch(
      getPositionHistories(
        account,
        factoryAddress,
        lendingPoolAddress,
        wantToken.tokenDecimal,
        borrowToken.tokenDecimal,
        chainId,
        library
      )
    );
  }, [
    account,
    wantToken,
    borrowToken,
    uniPoolAddress,
    factoryAddress,
    lendingPoolAddress,
    chainId,
    library,
    dispatch,
  ]);

  useEffect(() => {
    if (account) {
      updateUserActivePositions();
      updateUserClosedPositions();
    } else {
      dispatch(clearUserRecords());
    }
  }, [account, dispatch, updateUserActivePositions, updateUserClosedPositions]);

  useEffect(() => {
    setMaxClosedPage(Math.ceil(allClosedPositions?.length / ITEM_PER_PAGE));
  }, [allClosedPositions]);

  useEffect(() => {
    if (allClosedPositions?.length > 0) {
      setClosedPos(
        allClosedPositions?.slice(
          (closedPage - 1) * ITEM_PER_PAGE,
          closedPage * ITEM_PER_PAGE
        )
      );
    }
  }, [closedPage, allClosedPositions]);

  useEffect(() => {
    const total = allActivePositions?.length;
    setMaxActivePage(Math.ceil(total / ITEM_PER_PAGE));
  }, [allActivePositions]);

  useEffect(() => {
    if (allActivePositions?.length > 0) {
      setActivePos(
        allActivePositions.slice(
          (activePage - 1) * ITEM_PER_PAGE,
          activePage * ITEM_PER_PAGE
        )
      );
    }
  }, [activePage, allActivePositions]);

  useEffect(() => {
    if (!account || !chainId || !isSupportedChain(chainId)) {
      return;
    }
    if (
      !ALL_PAIRS[chainId].find((pair) => pair.factoryAddress === factoryAddress)
    )
      return;
    dispatch(
      getPositionInfos(
        account,
        factoryAddress,
        lendingPoolAddress,
        wantToken.tokenDecimal,
        borrowToken.tokenDecimal,
        chainId,
        library
      )
    );
  }, [
    account,
    lendingPoolAddress,
    wantToken.tokenDecimal,
    borrowToken.tokenDecimal,
    factoryAddress,
    chainId,
    library,
    dispatch,
  ]);

  return (
    <Container>
      <TitleRow>
        <Subtitle>Your UniBot</Subtitle>
        <Switchers>
          <Switcher
            isSelect={isShowActive}
            onClick={() => setIsShowActive(true)}
          >
            Current
          </Switcher>
          <Switcher
            isSelect={!isShowActive}
            onClick={() => setIsShowActive(false)}
          >
            History
          </Switcher>
        </Switchers>
      </TitleRow>
      {/* Active Positions */}
      {isShowActive && (
        <Fragment>
          <RowContainer>
            {currHeads.map((head) => (
              <LabelContainer key={head.label}>
                <LabelWithHint
                  labelComponent={<HeadText>{head.label}</HeadText>}
                  hintText={head.hint}
                />
              </LabelContainer>
            ))}
            <BtnContainer></BtnContainer>
          </RowContainer>
          {isUnderCorrectNetwork && allActivePositions?.length > 0 ? (
            <Fragment>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={activePage}
              >
                <PositionsContainer>
                  {activePos.map((position) => (
                    <ActivePosition
                      key={position.positionId}
                      position={position}
                      pair={pair}
                      currency={currency}
                      decimal={decimal}
                      updateUserActivePositions={updateUserActivePositions}
                      updateUserClosedPositions={updateUserClosedPositions}
                      updateVaultBalance={updateVaultBalance}
                      vaultBalance={vaultBalance}
                      uniPoolState={uniPoolState}
                    />
                  ))}
                </PositionsContainer>
              </motion.div>
              <Pagination
                currPage={activePage}
                maxPage={maxActivePage}
                setPage={setActivePage}
              />
            </Fragment>
          ) : (
            <NoRecordContainer>
              <NoRecord>No Record</NoRecord>
            </NoRecordContainer>
          )}
        </Fragment>
      )}
      {/* Closed Positions */}
      {!isShowActive && (
        <Fragment>
          <RowContainer>
            {hisHeads.map((head) => (
              <LabelContainer key={head.label}>
                <LabelWithHint
                  labelComponent={<HeadText>{head.label}</HeadText>}
                  hintText={head.hint}
                />
              </LabelContainer>
            ))}
            <BtnContainerH />
          </RowContainer>
          {isUnderCorrectNetwork && allClosedPositions?.length > 0 ? (
            <Fragment>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={closedPage}
              >
                <PositionsContainer>
                  {closedPos.map((position) => (
                    <ClosedPosition
                      key={position.positionId}
                      position={position}
                      pair={pair}
                    />
                  ))}
                </PositionsContainer>
              </motion.div>
              <Pagination
                currPage={closedPage}
                maxPage={maxClosedPage}
                setPage={setClosedPage}
              />
            </Fragment>
          ) : (
            <NoRecordContainer>
              <NoRecord>No Record</NoRecord>
            </NoRecordContainer>
          )}
        </Fragment>
      )}
    </Container>
  );
};

export default YourBot;

const Container = styled.div`
  padding: 24px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Subtitle = styled(H2)`
  font-size: 20px;
  line-height: 32px;
  text-align: left;
  color: ${Colors.gray1};
  font-family: TT Firs Neue, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  margin-bottom: 12px;
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 0px;
  }
`;

const Switchers = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  background-color: ${Colors.backgroundGray4};
`;

const Switcher = styled.button<{ isSelect: boolean }>`
  font-size: 13px;
  line-height: 20px;
  padding: 2px 20px;
  border-radius: 100px;
  /* font-weight: ${(props) => (props.isSelect ? "bold" : "normal")}; */
  color: ${(props) => (props.isSelect ? Colors.lightKhaki : Colors.gray4)};
  border: 1px solid
    ${(props) => (props.isSelect ? Colors.lightKhaki : "transparent")};
  background-color: ${(props) =>
    props.isSelect ? "transparent" : "transparent"};
`;

const LabelContainer = styled.div`
  flex: 1 1 160px;
`;

const HeadText = styled(H4)`
  display: block;
  color: ${Colors.gray3};
  font-size: 13px;
  line-height: 22px;
  font-weight: bold;
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    font-weight: normal;
  }
`;

const RowContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${Colors.gray3};
  @media (max-width: 768px) {
    display: none;
  }
`;

const BtnContainer = styled.div`
  flex: 1 1 160px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  @media (max-width: 768px) {
    flex: 0 0 200px;
  }
  button {
    margin: 0;
  }
  @media (max-width: 576px) {
    width: 100%;
    flex: initial;
    margin: 12px 0 18px 0;
    gap: 12px;
    button {
      width: 100%;
    }
  }
`;

const NoRecordContainer = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const NoRecord = styled.span`
  display: inline-block;
  padding: 4px 20px;
  border-radius: 8px;
  background-color: ${Colors.backgroundGray4};
  margin: auto;
  font-size: 14px;
  line-height: 22px;
  color: ${Colors.ivory};
`;

const BtnContainerH = styled.div`
  flex: 0 0 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  @media (max-width: 768px) {
    width: 100%;
    flex: initial;
    margin: 6px 0;
  }
`;

const PositionsContainer = styled.div`
  > div:not(:last-child) {
    border-bottom: 0.5px solid ${Colors.gray4};
  }
`;
